import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../App';
import UpgradeScreen from '../components/UpgradeScreen';
import { hasFullAccess } from '../lib/platform';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Play, 
  Clock, 
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  LogOut,
  Menu,
  X,
  Calculator,
  Users,
  Mic,
  Video,
  Award,
  Target,
  Building2,
  Banknote,
  Scale,
  Wrench,
  ShieldCheck,
  Bot,
  GraduationCap,
  Gift,
  Headphones,
  Radio,
  Sparkles,
  Search,
  PlayCircle,
  Pause,
  Volume2,
  Star,
  TrendingUp,
  Podcast
} from 'lucide-react';

// Podcast data organized by themes
const PODCAST_THEMES = [
  {
    id: 'acquisition',
    title: 'Acquisition & stratégie',
    icon: Building2,
    color: 'blue',
    gradient: 'from-blue-600 to-indigo-700',
    podcasts: [
      {
        id: 'P01',
        title: 'Location : longue, courte ou moyenne ?',
        summary: 'Choisir le bon mode de location selon ton objectif (stabilité, cashflow, contraintes) et comprendre pourquoi la moyenne durée devient un "sweet spot".',
        learnings: [
          'Les critères qui tranchent vraiment (temps, risque, zone, vacance, réglementation)',
          'Ce qui marche en courte durée aujourd\'hui (standards + process)',
          'La logique moyenne durée + stratégie hybride selon les saisons'
        ],
        duration: 25,
        videoUrl: 'https://customer-assets.emergentagent.com/job_1dae8851-70c6-45a8-a7e5-858edaeb24e4/artifacts/w0u94944_Podcast%20-%20David%20-%20V1.mp4',
        guest: 'David'
      },
      {
        id: 'P02',
        title: 'MDB : sécuriser une opération rentable',
        summary: 'Comprendre le métier de marchand de biens, où se joue la marge, et comment sécuriser une opération (risques, fiscal, plan B).',
        learnings: [
          'Les étapes d\'une opération MDB "de la prospection à la sortie"',
          'Les risques majeurs + garde-fous concrets (marché, travaux, délais, juridique)',
          'Les points clés juridiques/fiscaux qui changent le net (structure, TVA, assurances)'
        ],
        duration: 24,
        videoUrl: 'https://customer-assets.emergentagent.com/job_3e0df213-3ec8-4a97-a80c-8a95cccc4c62/artifacts/7n0n3xtc_Podcast%20-%20Marie%20Laure%20-%20V1.mp4',
        guest: 'Marie-Laure'
      },
      {
        id: 'P03',
        title: 'MDB : réno pilotée par l\'objectif (cas réel)',
        summary: 'Méthode "objectif → stratégie → rénovation → exécution" + étude de cas chiffrée (Airbnb grande capacité).',
        learnings: [
          'Comment prioriser les dépenses pour sécuriser le CA (et éviter la réno "au feeling")',
          'Les décisions qui font varier le remplissage (expérience, photos, avis)',
          'Comment prévoir un plan B si le marché se durcit (moyenne durée / coliving / etc.)'
        ],
        duration: 27,
        videoUrl: 'https://customer-assets.emergentagent.com/job_3e0df213-3ec8-4a97-a80c-8a95cccc4c62/artifacts/yj7elavp_Podcast%20-%20Radu%20-%20V1.mp4',
        guest: 'Radu'
      }
    ]
  },
  {
    id: 'financement',
    title: 'Financement',
    icon: Banknote,
    color: 'emerald',
    gradient: 'from-emerald-600 to-teal-700',
    podcasts: [
      {
        id: 'P04',
        title: 'Dossier banque : méthode "banquable"',
        summary: 'Construire un dossier clair et solide, éviter les erreurs éliminatoires, et savoir quand activer un courtier.',
        learnings: [
          'La règle n°1 pour augmenter tes chances (simulation réaliste + cohérence)',
          'Ce que la banque "lit" dans les relevés et ce qui bloque vraiment',
          'Présenter le dossier sans "griller" sa demande (timing + ordre des banques)'
        ],
        duration: 29,
        videoUrl: 'https://customer-assets.emergentagent.com/job_3e0df213-3ec8-4a97-a80c-8a95cccc4c62/artifacts/33blos4s_Podcast%20-%20Aure%CC%81lien%20-%20V2.mp4',
        guest: 'Aurélien'
      },
      {
        id: 'P05',
        title: 'Dossiers atypiques : SCI & DPE F/G',
        summary: 'Rendre finançable un dossier hors cadre (SCI/SCI familiale, revenus complexes, bien énergivore) avec une présentation "rassurante".',
        learnings: [
          'Ce qui déclenche les alertes banque (structure, flux, garanties, timing)',
          'La storyline bancaire : projet → chiffres → risques → plans de mitigation',
          'Intégrer les travaux énergétiques comme un plan maîtrisé (devis, calendrier, valeur)'
        ],
        duration: 27,
        videoUrl: 'https://customer-assets.emergentagent.com/job_3e0df213-3ec8-4a97-a80c-8a95cccc4c62/artifacts/pbjm8ban_Podcast%20-%20Alicia%20-%20V2.mp4',
        guest: 'Alicia'
      }
    ]
  },
  {
    id: 'juridique',
    title: 'Juridique & copropriété',
    icon: Scale,
    color: 'purple',
    gradient: 'from-purple-600 to-violet-700',
    podcasts: [
      {
        id: 'P06',
        title: 'Notaire : méthode "zéro surprise"',
        summary: 'Anticiper les blocages au compromis/acte : copro, servitudes, urbanisme, fiscalité, risques… et savoir quand appeler le notaire.',
        learnings: [
          'La checklist "avant mise en vente/avant achat" (documents critiques)',
          'Travaux/urbanisme : ce qui doit être déclaré, prouvé, régularisé',
          'Fiscalité vendeur + points techniques sensibles (assainissement, ERP, sinistres)'
        ],
        duration: 23,
        videoUrl: 'https://customer-assets.emergentagent.com/job_1dae8851-70c6-45a8-a7e5-858edaeb24e4/artifacts/2nwe0hzk_Podcast%20-%20Justine%20et%20Sonia%20-%20V1.mp4',
        guest: 'Justine et Sonia'
      },
      {
        id: 'P07',
        title: 'Avocate : sécuriser dès l\'offre (mini-audit)',
        summary: 'Sécuriser un investissement dès l\'offre : comprendre ce qu\'on achète (droits/charges), repérer les drapeaux rouges, structurer clauses & conditions.',
        learnings: [
          'Lecture rapide des docs (titre, copro, destination, servitudes)',
          'Grille "occupation & baux" : durée, coût de sortie, risques (pro/commercial)',
          'Faisabilité urbanisme + passifs techniques/contentieux + plan de repli'
        ],
        duration: 27,
        videoUrl: 'https://customer-assets.emergentagent.com/job_1dae8851-70c6-45a8-a7e5-858edaeb24e4/artifacts/u4vlumlq_Podcast%20-%20Julie%20-%20V1.mp4',
        guest: 'Julie'
      },
      {
        id: 'P08',
        title: 'Copropriété : EDD, tantièmes, pièges',
        summary: 'Comprendre la copropriété "en vrai" (RC, EDD, tantièmes), éviter les erreurs qui coûtent cher, et savoir quand faire intervenir un géomètre-expert.',
        learnings: [
          'Ce qu\'il faut lire en priorité en 10 minutes avant achat',
          'Les erreurs fréquentes (annexes, charges, restrictions, travaux sans autorisation)',
          'Diviser/modifier un lot : étapes et points de vigilance'
        ],
        duration: 22,
        videoUrl: 'https://customer-assets.emergentagent.com/job_3e0df213-3ec8-4a97-a80c-8a95cccc4c62/artifacts/wlupfwgb_Podcast%20-%20Mathieu%20-%20V1.mp4',
        guest: 'Matthieu'
      }
    ]
  },
  {
    id: 'travaux',
    title: 'Travaux & rénovation',
    icon: Wrench,
    color: 'orange',
    gradient: 'from-orange-500 to-amber-600',
    podcasts: [
      {
        id: 'P09',
        title: 'Archi : l\'entonnoir de l\'idée au chantier',
        summary: 'La méthode "entonnoir" pour éviter 80% des galères : concepts → plan fonctionnel → matériaux → dossier artisans → chantier.',
        learnings: [
          'Les erreurs de plan les plus coûteuses (circulation, rangements, cuisine/SDE)',
          'Faire du style sans exploser le budget (choix à fort impact visuel)',
          'Pourquoi un bon dossier artisans rend les devis comparables et sécurise le chantier'
        ],
        duration: 23,
        videoUrl: 'https://customer-assets.emergentagent.com/job_1dae8851-70c6-45a8-a7e5-858edaeb24e4/artifacts/kl9m3ufb_Podcast%20-%20Bruno%20-%20V2.mp4',
        guest: 'Bruno'
      },
      {
        id: 'P10',
        title: 'Contractant : un pilote un chantier sécurisé',
        summary: 'Comprendre le rôle du contractant général et comment l\'interlocuteur unique sécurise délais, budget, qualité (et stress).',
        learnings: [
          'Différence entreprise générale vs contractant général',
          'Process "entonnoir" : découverte → choix judicieux → optimisation temps/coûts',
          'Gestion des changements : ordre de service, avenants, achèvement'
        ],
        duration: 25,
        videoUrl: 'https://customer-assets.emergentagent.com/job_1dae8851-70c6-45a8-a7e5-858edaeb24e4/artifacts/ftk8avye_Podcast%20Jean-Louis%20-%20V1.mp4',
        guest: 'Jean-Louis'
      },
      {
        id: 'P11',
        title: 'Travaux : sécuriser budget/délais/qualité',
        summary: 'Sécuriser un chantier sans se faire piéger (plateformes, devis flous, assurances) + méthode en 4 étapes.',
        learnings: [
          'Les dangers invisibles des plateformes / devis incomparables',
          'Les 3 non-négociables avant de lancer (objectif, cahier des charges, enveloppe)',
          'La méthode : cadrage → consultation → arbitrage → exécution'
        ],
        duration: 26,
        videoUrl: 'https://customer-assets.emergentagent.com/job_3e0df213-3ec8-4a97-a80c-8a95cccc4c62/artifacts/vtrvukec_Podcast%20-%20Alexandre%20-%20V1.mp4',
        guest: 'Alexandre'
      }
    ]
  },
  {
    id: 'conformite',
    title: 'Conformité',
    icon: ShieldCheck,
    color: 'red',
    gradient: 'from-red-500 to-rose-600',
    podcasts: [
      {
        id: 'P12',
        title: 'DPE : leviers et décisions (sans se ruiner)',
        summary: 'Comprendre le DPE actuel, les dates repères, et les leviers concrets pour gagner une lettre sans se ruiner (méthode en 4 étapes).',
        learnings: [
          'Les changements et points de vigilance (validité, ajustements, surprises)',
          'Les meilleurs "ratio € / gain DPE" et les fausses bonnes idées',
          'La méthode : diagnostic → plan → priorités → re-DPE'
        ],
        duration: 28,
        videoUrl: 'https://customer-assets.emergentagent.com/job_1dae8851-70c6-45a8-a7e5-858edaeb24e4/artifacts/dghpblvh_Podcast%20-%20Guillaume%20-%20V1.mp4',
        guest: 'Guillaume'
      }
    ]
  },
  {
    id: 'assurances',
    title: 'Assurances & protection',
    icon: ShieldCheck,
    color: 'teal',
    gradient: 'from-teal-500 to-cyan-600',
    podcasts: [
      {
        id: 'P13',
        title: 'Assurance emprunteur : garanties & pièges',
        summary: 'Comprendre ce qui protège vraiment (DC/PTIA/IPT/ITT), éviter les fausses économies, et sécuriser la délégation sans refus banque.',
        learnings: [
          'Les garanties clés + pièges (franchises, exclusions, forfaitaire/indemnitaire)',
          'Comment éviter un refus "bête" d\'équivalence bancaire (critères CCSF)',
          'Optimiser sans fragiliser (et quand il faut dire non)'
        ],
        duration: 24,
        videoUrl: 'https://customer-assets.emergentagent.com/job_3e0df213-3ec8-4a97-a80c-8a95cccc4c62/artifacts/87j2oczi_Podcast%20-%20Yoan%20-%20V1.mp4',
        guest: 'Yoan'
      },
      {
        id: 'P14',
        title: 'Protection sociale & épargne',
        summary: 'Construire une stratégie simple pour protéger activité + famille, puis structurer épargne/transmission (sans jargon).',
        learnings: [
          'Les angles morts des investisseurs/entrepreneurs (arrêt, invalidité, décès)',
          'Prioriser en 3 étages : pro → famille → épargne/transmission',
          'Épargne cohérente (AV/PEA) sans faire "juste de la défisc"'
        ],
        duration: 24,
        videoUrl: 'https://customer-assets.emergentagent.com/job_3e0df213-3ec8-4a97-a80c-8a95cccc4c62/artifacts/70bf331u_Podcast%20-%20Sandrine%20-%20V1.mp4',
        guest: 'Sandrine'
      }
    ]
  },
  {
    id: 'tech',
    title: 'Tech / IA / automatisation',
    icon: Bot,
    color: 'indigo',
    gradient: 'from-indigo-600 to-purple-700',
    podcasts: [
      {
        id: 'P15',
        title: 'Automatiser (process & fiabilité)',
        summary: 'Les diagnostics sont un système de conformité : centraliser, tracer, alerter, fiabiliser… et automatiser pour réduire retards et litiges.',
        learnings: [
          'Pourquoi un diagnostic n\'est pas "un PDF" mais une responsabilité (validité, preuves)',
          'Les risques de dispersion (versions, périmés, incohérences entre docs)',
          'Ce que l\'automatisation change (tableaux de bord, alertes, rapports)'
        ],
        duration: 26,
        videoUrl: 'https://customer-assets.emergentagent.com/job_1dae8851-70c6-45a8-a7e5-858edaeb24e4/artifacts/uelhhaym_Podcast%20-%20Dorian%20-%20V1.mp4',
        guest: 'Dorian'
      },
      {
        id: 'P16',
        title: 'IA & immobilier 2026 : l\'agent augmenté',
        summary: 'L\'IA ne remplace pas l\'expert : elle "nettoie l\'agenda". Approche pro : diagnostic des process, intégration, sécurité des données.',
        learnings: [
          'Commencer par un "diagnostic opérationnel" (ne pas digitaliser le désordre)',
          'Automatisations utiles (qualification leads, analyse docs, contenu)',
          'Interopérabilité + sécurité/RGPD (données locales, conformité)'
        ],
        duration: 25,
        videoUrl: 'https://customer-assets.emergentagent.com/job_1dae8851-70c6-45a8-a7e5-858edaeb24e4/artifacts/vx6um1os_Podcast%20-%20Laura%20-%20V1.mp4',
        guest: 'Laura'
      }
    ]
  },
  {
    id: 'patrimoine-bonus',
    title: 'Patrimoine Bonus',
    icon: Gift,
    color: 'amber',
    gradient: 'from-amber-500 to-yellow-500',
    podcasts: [
      {
        id: 'P17',
        title: 'CGP : quand, pourquoi, comment (concret)',
        summary: 'À quoi sert un CGP, pour qui c\'est pertinent, comment se déroule l\'accompagnement et combien ça coûte.',
        learnings: [
          'Pourquoi consulter (stratégie, cohérence, arbitrages)',
          'Comment se passent les rendez-vous et la méthode de travail',
          'Les cas pratiques typiques et comment contacter'
        ],
        duration: 22,
        videoUrl: 'https://customer-assets.emergentagent.com/job_3e0df213-3ec8-4a97-a80c-8a95cccc4c62/artifacts/q92liux1_Podcast%20-%20Nathalie%20-%20V1.mp4',
        guest: 'Nathalie'
      },
      {
        id: 'P18',
        title: 'Fiscalité & équipe : bien s\'entourer',
        summary: 'Déficit foncier, critères d\'achat MDB, et comment constituer son équipe de spécialistes au bon moment.',
        learnings: [
          'Déficit foncier (le bon usage) : quand c\'est pertinent, quelles dépenses comptent réellement, et comment éviter de "faire des travaux pour défiscaliser" sans stratégie',
          'Choisir un bien pour une acquisition en marchand de biens : les critères non négociables',
          'S\'entourer de spécialistes : à quel moment activer notaire/avocat/architecte/courtier/expert-comptable'
        ],
        duration: 20,
        videoUrl: 'https://customer-assets.emergentagent.com/job_1dae8851-70c6-45a8-a7e5-858edaeb24e4/artifacts/4bznuoab_Podcast%20-%20Laurent%20-%20V1.mp4',
        guest: 'Laurent'
      }
    ]
  }
];

// Calculate total stats
const totalEpisodes = PODCAST_THEMES.reduce((acc, theme) => acc + theme.podcasts.length, 0);
const totalDuration = PODCAST_THEMES.reduce((acc, theme) => 
  acc + theme.podcasts.reduce((sum, p) => sum + p.duration, 0), 0);
const availableEpisodes = PODCAST_THEMES.reduce((acc, theme) => 
  acc + theme.podcasts.filter(p => p.videoUrl).length, 0);

const ClassroomPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [expandedTheme, setExpandedTheme] = useState(null);
  const [selectedPodcast, setSelectedPodcast] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const isClubMember = hasFullAccess(user?.subscription_tier);

  // Show upgrade screen for non-club members
  if (!isClubMember) {
    return (
      <UpgradeScreen
        icon={Mic}
        iconBgClass="from-slate-800 to-slate-900"
        iconColorClass="text-amber-500"
        buttonGradientClass="from-amber-500 to-orange-600"
        title="Studio Podcast Investisseurs"
        currentTier={user?.subscription_tier}
        features={[
          { icon: Headphones, title: "17 épisodes exclusifs", description: "Échanges avec les experts du réseau 10M Investor" },
          { icon: Target, title: "Stratégies concrètes", description: "Acquisition, financement, juridique, travaux..." },
          { icon: Star, title: "+7h de contenu", description: "Format podcast vidéo, regardez quand vous voulez" }
        ]}
      />
    );
  }

  const toggleTheme = (themeId) => {
    setExpandedTheme(expandedTheme === themeId ? null : themeId);
  };

  const openPodcast = (podcast) => {
    setSelectedPodcast(podcast);
  };

  const closePodcast = () => {
    setSelectedPodcast(null);
  };

  // Filter podcasts based on search
  const filteredThemes = searchQuery.trim() 
    ? PODCAST_THEMES.map(theme => ({
        ...theme,
        podcasts: theme.podcasts.filter(p => 
          p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.guest?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(theme => theme.podcasts.length > 0)
    : PODCAST_THEMES;

  // Podcast detail view - Studio style
  if (selectedPodcast) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
        {/* Ambient background effects */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        </div>

        {/* Header */}
        <header className="relative bg-slate-900 border-b border-slate-800 sticky top-0 z-50 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={closePodcast} 
                className="text-slate-400 hover:text-amber-400 hover:bg-slate-800/50"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Retour au studio
              </Button>
              <div className="flex items-center gap-3">
                <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30">
                  <Radio className="w-3 h-3 mr-1.5 animate-pulse" />
                  {selectedPodcast.id}
                </Badge>
                <Badge className="bg-slate-800 text-slate-300 border border-slate-700">
                  <Clock className="w-3 h-3 mr-1.5" />
                  {selectedPodcast.duration} min
                </Badge>
              </div>
            </div>
          </div>
        </header>

        <div className="relative max-w-5xl mx-auto px-4 py-8">
          {/* Video Player - Studio Style */}
          <div className="relative mb-8">
            <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-amber-500/20 rounded-2xl blur-xl opacity-50" />
            <div className="relative aspect-video bg-slate-900 rounded-2xl overflow-hidden border border-slate-800 shadow-2xl shadow-black/50">
              {selectedPodcast.videoUrl ? (
                <video
                  src={selectedPodcast.videoUrl}
                  controls
                  className="w-full h-full"
                  poster=""
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 to-slate-950">
                  <div className="relative">
                    <div className="absolute inset-0 bg-amber-500/20 rounded-full blur-2xl animate-pulse" />
                    <div className="relative w-32 h-32 bg-gradient-to-br from-slate-800 to-slate-900 rounded-full flex items-center justify-center border border-slate-700">
                      <Mic className="w-16 h-16 text-amber-500/60" />
                    </div>
                  </div>
                  <p className="text-xl font-medium text-slate-400 mt-6">Épisode à venir</p>
                  <p className="text-sm text-slate-500 mt-2">Cette vidéo sera bientôt disponible</p>
                </div>
              )}
            </div>
          </div>

          {/* Podcast Info - Studio Card */}
          <div className="relative mb-6">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500/30 to-orange-500/30 rounded-2xl blur opacity-30" />
            <div className="relative bg-slate-900/90 backdrop-blur-xl rounded-2xl border border-slate-800 p-8">
              <div className="flex items-start gap-6">
                <div className="hidden sm:flex w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/20">
                  <Headphones className="w-10 h-10 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <Badge className="bg-amber-500/10 text-amber-400 border border-amber-500/30 text-xs">
                      {selectedPodcast.id}
                    </Badge>
                    {selectedPodcast.guest && (
                      <Badge className="bg-slate-800 text-slate-300 border border-slate-700 text-xs">
                        Avec {selectedPodcast.guest}
                      </Badge>
                    )}
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                    {selectedPodcast.title}
                  </h1>
                  <p className="text-slate-400 text-lg leading-relaxed">{selectedPodcast.summary}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Learnings - Studio Style */}
          <div className="relative">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-2xl blur opacity-30" />
            <div className="relative bg-slate-900/90 backdrop-blur-xl rounded-2xl border border-slate-800 p-8">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center mr-3">
                  <GraduationCap className="w-5 h-5 text-emerald-400" />
                </div>
                Ce que tu vas apprendre
              </h2>
              <div className="space-y-4">
                {selectedPodcast.learnings.map((learning, index) => (
                  <div key={index} className="flex items-start gap-4 group">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/20 flex items-center justify-center flex-shrink-0 group-hover:from-amber-500/30 group-hover:to-orange-500/30 transition-all">
                      <span className="text-sm font-bold text-amber-400">{index + 1}</span>
                    </div>
                    <p className="text-slate-300 pt-2 leading-relaxed">{learning}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main podcast list view - Studio Design
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Ambient background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-[600px] bg-gradient-to-b from-amber-500/5 to-transparent" />
        <div className="absolute top-20 right-10 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute top-40 left-10 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl" />
      </div>

      {/* Header - Dark Studio Style */}
      <header className="relative bg-slate-900/80 backdrop-blur-xl border-b border-slate-800/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-amber-400 hover:bg-slate-800/50">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Tableau de bord
                </Button>
              </Link>
            </div>
            <div className="flex items-center space-x-3">
              <Link to="/profile" className="hidden md:flex items-center space-x-2 px-3 py-2 rounded-xl hover:bg-slate-800/50 transition-colors">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white text-sm font-medium shadow-lg shadow-amber-500/20">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <span className="text-sm font-medium text-slate-300">{user?.name}</span>
              </Link>
              <Button variant="ghost" size="sm" onClick={logout} className="hidden md:flex text-slate-500 hover:text-red-400 hover:bg-slate-800/50">
                <LogOut className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="md:hidden text-slate-400" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-900/95 backdrop-blur-xl border-t border-slate-800 py-4 px-4 space-y-2">
            <Link to="/dashboard" className="block px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">Tableau de bord</Link>
            <Link to="/calculator" className="block px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">Calculateur</Link>
            <Link to="/community" className="block px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">Communauté</Link>
            <Link to="/profile" className="block px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">Mon Profil</Link>
            <button onClick={logout} className="w-full text-left px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors">Déconnexion</button>
          </div>
        )}
      </header>

      {/* Hero - Studio Style */}
      <div className="relative py-16 sm:py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Left side - Title & Info */}
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full mb-6">
                <Radio className="w-4 h-4 text-amber-400 animate-pulse" />
                <span className="text-amber-400 text-sm font-medium">Studio Podcast</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4 tracking-tight">
                <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500 bg-clip-text text-transparent">
                  10M INVESTOR
                </span>
                <br />
                <span className="text-white">PODCAST</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-400 mb-8 max-w-xl mx-auto lg:mx-0">
                Les coulisses de l'investissement immobilier avec les experts du réseau. 
                Stratégies concrètes, retours d'expérience, et conseils actionnables.
              </p>

              {/* Stats */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 mb-8">
                <div className="flex items-center gap-3 px-4 py-3 bg-slate-800/50 rounded-xl border border-slate-700/50">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                    <Headphones className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">{totalEpisodes}</div>
                    <div className="text-xs text-slate-500">Épisodes</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 px-4 py-3 bg-slate-800/50 rounded-xl border border-slate-700/50">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">{Math.round(totalDuration / 60)}h+</div>
                    <div className="text-xs text-slate-500">De contenu</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 px-4 py-3 bg-slate-800/50 rounded-xl border border-slate-700/50">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <PlayCircle className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">{availableEpisodes}</div>
                    <div className="text-xs text-slate-500">Disponibles</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side - Visual Element */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500/30 to-orange-500/30 rounded-full blur-3xl" />
              <div className="relative w-64 h-64 sm:w-80 sm:h-80">
                {/* Outer ring */}
                <div className="absolute inset-0 rounded-full border-2 border-amber-500/20 animate-[spin_20s_linear_infinite]" />
                <div className="absolute inset-4 rounded-full border border-orange-500/30 animate-[spin_15s_linear_infinite_reverse]" />
                
                {/* Center microphone */}
                <div className="absolute inset-8 bg-gradient-to-br from-slate-800 to-slate-900 rounded-full flex items-center justify-center border border-slate-700 shadow-2xl shadow-black/50">
                  <div className="relative">
                    <div className="absolute inset-0 bg-amber-500/20 rounded-full blur-xl animate-pulse" />
                    <Mic className="relative w-20 h-20 sm:w-28 sm:h-28 text-amber-500" />
                  </div>
                </div>

                {/* Floating badges */}
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-slate-800 rounded-full border border-slate-700 shadow-lg">
                  <span className="text-xs font-medium text-amber-400">LIVE</span>
                </div>
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-slate-800 rounded-full border border-slate-700 shadow-lg">
                  <span className="text-xs font-medium text-slate-400">STUDIO</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4 mb-8">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <Input
            type="text"
            placeholder="Rechercher un épisode, un thème, un invité..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-6 bg-slate-800/50 border-slate-700 rounded-xl text-white placeholder:text-slate-500 focus:border-amber-500/50 focus:ring-amber-500/20"
          />
        </div>
      </div>

      {/* Podcast Themes - Studio Cards */}
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="space-y-4">
          {filteredThemes.map((theme) => {
            const IconComponent = theme.icon;
            const isExpanded = expandedTheme === theme.id;
            
            return (
              <div key={theme.id} className="relative group">
                {/* Glow effect on hover */}
                <div className={`absolute -inset-0.5 bg-gradient-to-r ${theme.gradient} rounded-2xl blur opacity-0 group-hover:opacity-20 transition-opacity duration-300`} />
                
                <Card className="relative bg-slate-900/80 backdrop-blur-xl border-slate-800 overflow-hidden rounded-2xl">
                  {/* Theme Header */}
                  <button
                    onClick={() => toggleTheme(theme.id)}
                    className="w-full text-left"
                    data-testid={`theme-${theme.id}`}
                  >
                    <div className="p-5 flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`w-14 h-14 bg-gradient-to-br ${theme.gradient} rounded-xl flex items-center justify-center shadow-lg`}>
                          <IconComponent className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-white">{theme.title}</h3>
                          <p className="text-slate-500 text-sm">
                            {theme.podcasts.length} épisode{theme.podcasts.length > 1 ? 's' : ''} 
                            <span className="mx-2">•</span>
                            {theme.podcasts.reduce((sum, p) => sum + p.duration, 0)} min
                          </p>
                        </div>
                      </div>
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${isExpanded ? 'bg-amber-500/20 rotate-180' : 'bg-slate-800'}`}>
                        <ChevronDown className={`w-5 h-5 ${isExpanded ? 'text-amber-400' : 'text-slate-500'}`} />
                      </div>
                    </div>
                  </button>

                  {/* Podcasts List */}
                  {isExpanded && (
                    <CardContent className="p-0 border-t border-slate-800">
                      <div className="divide-y divide-slate-800/50">
                        {theme.podcasts.map((podcast) => (
                          <button
                            key={podcast.id}
                            onClick={() => openPodcast(podcast)}
                            className="w-full text-left p-5 hover:bg-slate-800/50 transition-all duration-200 group/item"
                            data-testid={`podcast-${podcast.id}`}
                          >
                            <div className="flex items-start gap-4">
                              {/* Play button */}
                              <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                                podcast.videoUrl 
                                  ? 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg shadow-amber-500/20 group-hover/item:scale-105' 
                                  : 'bg-slate-800 border border-slate-700'
                              }`}>
                                {podcast.videoUrl ? (
                                  <Play className="w-6 h-6 text-white ml-0.5" />
                                ) : (
                                  <Clock className="w-6 h-6 text-slate-500" />
                                )}
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center flex-wrap gap-2 mb-2">
                                  <Badge className="bg-slate-800 text-slate-300 border border-slate-700 text-xs">
                                    {podcast.id}
                                  </Badge>
                                  <span className="text-xs text-slate-500 flex items-center">
                                    <Clock className="w-3 h-3 mr-1" />
                                    {podcast.duration} min
                                  </span>
                                  {podcast.guest && (
                                    <Badge className="bg-amber-500/10 text-amber-400 border border-amber-500/20 text-xs">
                                      {podcast.guest}
                                    </Badge>
                                  )}
                                  {!podcast.videoUrl && (
                                    <Badge className="bg-slate-700/50 text-slate-400 border-0 text-xs">
                                      Bientôt
                                    </Badge>
                                  )}
                                </div>
                                <h4 className="font-semibold text-white mb-1 group-hover/item:text-amber-400 transition-colors">
                                  {podcast.title}
                                </h4>
                                <p className="text-sm text-slate-500 line-clamp-2">{podcast.summary}</p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  )}
                </Card>
              </div>
            );
          })}
        </div>

        {/* Empty state for search */}
        {filteredThemes.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-10 h-10 text-slate-600" />
            </div>
            <p className="text-slate-400 text-lg">Aucun épisode trouvé</p>
            <p className="text-slate-500 text-sm mt-2">Essayez avec d'autres mots-clés</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassroomPage;
